import union from 'lodash/union';
import memoizeOne from 'memoize-one';
import { isOptimisticIssue } from '@atlassian/jira-business-issue-create/src/controllers/issue-create-context/index.tsx';
import type {
	ChildrenQueryData,
	ExpandedItems,
} from '@atlassian/jira-business-list-handlers/src/controllers/children/types.tsx';
import { toIssueId, type IssueId } from '@atlassian/jira-shared-types/src/general.tsx';
import { EXPLICIT } from '@atlassian/jira-software-roadmap-timeline-table-kit/src/common/constants/date.tsx';
import { getPlaceholder } from '@atlassian/jira-software-roadmap-timeline-table-kit/src/common/utils/bar/placeholder.tsx';
import type { InteractiveChartItem } from '@atlassian/jira-software-roadmap-timeline-table-kit/src/controllers/table-providers/chart-item-interaction/common/types.tsx';
import type { CreateItemAnchor } from '@atlassian/timeline-table/common/types/create';
import type { Item } from '@atlassian/timeline-table/common/types/item';
import {
	TIMELINE_PARENT_LEVEL,
	TIMELINE_CHILD_LEVEL,
	TIMELINE_EPIC_HIERARCHY_LEVEL,
} from '../../../common/constants.tsx';
import type { Hash, ChartDataHash } from '../../../common/types.tsx';
import { getColor } from '../../../common/utils/get-color.tsx';
import { getDependenciesItems } from '../../../common/utils/get-dependencies-items.tsx';
import { getItemOffsetPositions } from '../../../common/utils/get-items-offsets/index.tsx';
import { isSubTaskLevel } from '../../../common/utils/item-level.tsx';
import { createItemSkeleton } from '../../../common/utils/item-skeleton.tsx';
import type { IssueParentMap } from '../../../common/utils/use-issue-parent-map.tsx';
import type { IssuesMap } from '../../../common/utils/use-issues-map.tsx';
import type {
	TimelineViewIssuesResult,
	TimelineViewIssue,
	TimelineViewIssuesReturn,
} from '../../../services/get-timeline-view-issues/types.tsx';
import type { TimelineData } from '../types.tsx';
import { transformDates } from './date.tsx';

type Params = {
	timelineViewIssuesData: TimelineViewIssuesResult;
	childrenQueryData: ChildrenQueryData<TimelineViewIssuesReturn>;
	expandedItems: ExpandedItems;
	createItemAnchor?: CreateItemAnchor;
	issuesMap: IssuesMap;
	issueParentMap: IssueParentMap;
	isLinkingEnabled: boolean | undefined;
	isFiltering?: boolean;
	isOverview?: boolean;
};

const shallowCompare = (newArg: unknown, lastArg: unknown) => {
	if (
		typeof newArg !== 'object' ||
		newArg === null ||
		typeof lastArg !== 'object' ||
		lastArg === null
	) {
		return newArg === lastArg;
	}

	// for object parameter, we want to do shallow compare on the top level arguments
	return (
		Object.keys(newArg).length === Object.keys(lastArg).length &&
		// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'. | TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'.
		Object.keys(newArg).every((key) => key in lastArg && newArg[key] === lastArg[key])
	);
};

const getTransformedTimelineData = ({
	timelineViewIssuesData,
	childrenQueryData,
	expandedItems,
	createItemAnchor,
	issuesMap,
	issueParentMap,
	isLinkingEnabled,
	isFiltering = false,
	isOverview = false,
}: Params) => {
	const { issues } = timelineViewIssuesData;
	const rootItemIds: IssueId[] = !isOverview ? issues.map((item) => item.issueId) : [];

	const childIdsHash: Hash<IssueId[]> = {};
	const chartDataHash: ChartDataHash = {};
	const startDateHash: Hash<number | undefined> = {};
	const dueDateHash: Hash<number | undefined> = {};
	const dependenciesHash: Hash<IssueId[]> = {};
	const interactiveChartItems: InteractiveChartItem[] = [];

	issuesMap.clear();
	issueParentMap.clear();

	const convertIssueToInteractiveChartItem = (
		item: TimelineViewIssue,
		parentId?: IssueId,
	): InteractiveChartItem => {
		// issueMap is used to access issueKey from custom Item component which is not accessible from its props
		issuesMap.set(item.issueId, item);

		// getting all dependencies and creating a hash used in getDependenciesItems()
		((isLinkingEnabled === true && item.issueLinks) || []).forEach((link) => {
			const { inward, outward } = link;
			const inwardId = inward?.issueId;
			const outwardId = outward?.issueId;
			if (inwardId && inwardId !== item.issueId) {
				dependenciesHash[item.issueId] = union(dependenciesHash[item.issueId], [inwardId]);
			}
			if (outwardId && outwardId !== item.issueId) {
				dependenciesHash[outwardId] = union(dependenciesHash[outwardId], [item.issueId]);
			}
		});

		// for the purpose of getDependenciesItems
		chartDataHash[item.issueId] = {
			color: getColor(item.status?.categoryId),
			...transformDates({ startDate: item.startDate, dueDate: item.dueDate }),
		};

		startDateHash[item.issueId] = item.startDate;
		dueDateHash[item.issueId] = item.dueDate;

		const hasLeftPlaceholder = !item.startDate;
		const hasRightPlaceholder = !item.dueDate;

		return {
			level: parentId ? TIMELINE_CHILD_LEVEL : TIMELINE_PARENT_LEVEL,
			id: item.issueId,
			...transformDates({ startDate: item.startDate, dueDate: item.dueDate }),
			placeholder: getPlaceholder(hasLeftPlaceholder, hasRightPlaceholder),
			startDateType: EXPLICIT,
			dueDateType: EXPLICIT,
			intervalId: undefined,
			parentId,
		};
	};

	const createTimelineItem = (item: TimelineViewIssue): Item => ({
		id: toIssueId(item.issueId),
		level: item.issueType.hierarchyLevel,
		isInTransition: isOptimisticIssue(Number(item.issueId)),
		canCreateChildOverride: !isSubTaskLevel(item.issueType.hierarchyLevel),
		childItems: recursivelyPopulateChildItems(item),
	});

	const recursivelyPopulateChildItems = (item: TimelineViewIssue): Item[] | undefined => {
		if (isFiltering || !item.hasChildren) {
			return undefined;
		}

		const childrenIssuesQueryData = childrenQueryData[item.issueId];

		if (!childIdsHash[item.issueId]) {
			childIdsHash[item.issueId] = [];
		}

		if (!childrenIssuesQueryData || childrenIssuesQueryData.loading) {
			const skeletonChildItem = createItemSkeleton({
				id: item.issueId,
				level: item.issueType.hierarchyLevel - 1,
			});

			childIdsHash[item.issueId].push(skeletonChildItem.id);

			return [{ ...skeletonChildItem, isInTransition: false }];
		}

		return childrenIssuesQueryData.data.issues.reduce<Item[]>((childItems, childIssue) => {
			childIdsHash[item.issueId].push(childIssue.issueId);
			issueParentMap.set(childIssue.issueId, item.issueId);
			interactiveChartItems.push(convertIssueToInteractiveChartItem(childIssue, item.issueId));

			childItems.push(createTimelineItem(childIssue));
			return childItems;
		}, []);
	};

	let items: Item[] = [];

	if (!isOverview) {
		items = issues.reduce<Item[]>((acc, item) => {
			interactiveChartItems.push(convertIssueToInteractiveChartItem(item));
			acc.push(createTimelineItem(item));
			return acc;
		}, []);
	} else {
		issues.forEach((item) => {
			rootItemIds.push(item.issueId);

			const populateChildItems = (): Item[] | undefined => {
				if (!isFiltering && item.hasChildren) {
					const childrenIssuesQueryData = childrenQueryData[item.issueId];

					if (!childrenIssuesQueryData || childrenIssuesQueryData.loading) {
						const skeletonChildItem = createItemSkeleton({
							id: item.issueId,
							level: TIMELINE_CHILD_LEVEL,
						});

						return [
							{
								id: skeletonChildItem.id,
								level: TIMELINE_CHILD_LEVEL,
								isInTransition: false,
							},
						];
					}

					const childItemsNew: Item[] = [];

					childrenIssuesQueryData.data.issues.forEach((childIssue) => {
						if (!childIdsHash[item.issueId]) {
							childIdsHash[item.issueId] = [];
						}
						childIdsHash[item.issueId].push(childIssue.issueId);

						// register the parent of this child issue
						issueParentMap.set(childIssue.issueId, item.issueId);
						interactiveChartItems.push(
							convertIssueToInteractiveChartItem(childIssue, item.issueId),
						);

						childItemsNew.push({
							id: toIssueId(childIssue.issueId),
							level: TIMELINE_CHILD_LEVEL,
							isInTransition: isOptimisticIssue(Number(childIssue.issueId)),
							canCreateChildOverride: false,
						});
					});

					return childItemsNew;
				}

				return undefined;
			};

			interactiveChartItems.push(convertIssueToInteractiveChartItem(item));

			const timelineItem: Item = {
				id: toIssueId(item.issueId),
				level: TIMELINE_PARENT_LEVEL,
				isInTransition: isOptimisticIssue(Number(item.issueId)),
			};

			if (item.issueType.hierarchyLevel >= TIMELINE_EPIC_HIERARCHY_LEVEL) {
				// do not allow to create children issues for epic types
				timelineItem.canCreateChildOverride = false;
			} else {
				timelineItem.childItems = populateChildItems();
			}

			items.push(timelineItem);
		});
	}

	const positionsHash = getItemOffsetPositions(
		rootItemIds,
		childIdsHash,
		expandedItems,
		createItemAnchor,
	);

	const dependencies =
		isLinkingEnabled === true
			? getDependenciesItems(
					positionsHash,
					chartDataHash,
					dependenciesHash,
					startDateHash,
					dueDateHash,
				)
			: [];

	return {
		items,
		issuesMap,
		issueParentMap,
		dependencies,
		interactiveChartItems,
	};
};

export const toTimelineData = memoizeOne(
	(params: Params): TimelineData => getTransformedTimelineData(params),
	(newArgs, lastArgs) => {
		if (newArgs.length !== lastArgs.length) {
			return false;
		}

		const equalityFn = shallowCompare;
		return newArgs.every((newArg, index) => equalityFn(newArg, lastArgs[index]));
	},
);
